import { useState, useEffect, useCallback } from 'react';
import React from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Users, Plus, Loader2, Trash2, Edit, ShieldCheck,
  CheckCircle, Save, RefreshCw, ChevronDown, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { moduleItems as menuStructure } from '../components/Layout';

const ROLES = [
  { val: 'admin', label: 'Administrador', color: 'bg-red-100 text-red-800' },
  { val: 'gerencia', label: 'Gerencia', color: 'bg-purple-100 text-purple-800' },
  { val: 'compras', label: 'Compras', color: 'bg-blue-100 text-blue-800' },
  { val: 'contabilidad', label: 'Contabilidad', color: 'bg-teal-100 text-teal-800' },
  { val: 'bodega', label: 'Bodega', color: 'bg-amber-100 text-amber-800' },
  { val: 'obra', label: 'Obra (Operario)', color: 'bg-yellow-100 text-yellow-800' },
  { val: 'rrhh', label: 'RRHH', color: 'bg-pink-100 text-pink-800' },
  { val: 'rrhh_encargado', label: 'RRHH Encargado', color: 'bg-rose-100 text-rose-800' },
  { val: 'terreno', label: 'Terreno', color: 'bg-orange-100 text-orange-800' },
  { val: 'consulta', label: 'Consulta', color: 'bg-gray-100 text-gray-800' },
];

const _PERM_LABELS = { ver: 'Ver', editar: 'Editar', vb: 'VB/Aprobar' };

export default function GestionUsuarios() {
  const [users, setUsers] = useState([]);
  const [modules, setModules] = useState([]);
  const [moduleActions, setModuleActions] = useState({});
  const [loading, setLoading] = useState(false);

  // Create/Edit modal
  const [modal, setModal] = useState({ open: false, mode: 'create', user: null });
  const [form, setForm] = useState({ email: '', nombre: '', password: '', rol: 'consulta', permisos: {} });
  const [saving, setSaving] = useState(false);
  // Roles custom (plantillas)
  const [customRoles, setCustomRoles] = useState([]);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [templateNombre, setTemplateNombre] = useState('');
  const [templateDescripcion, setTemplateDescripcion] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, modsRes] = await Promise.all([
        api.get('/admin/users'),
        api.get('/admin/modules'),
      ]);
      setUsers(usersRes.data.users || []);
      setModules(modsRes.data.modules || []);
      setModuleActions(modsRes.data.module_actions || {});
      setCustomRoles(modsRes.data.custom_roles || []);
    } catch (err) {
      if (err.response?.status === 403) toast.error('Solo administradores pueden acceder');
      else toast.error('Error cargando datos');
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const loadDefaultPerms = async (rol) => {
    try {
      const res = await api.get(`/admin/default-permissions/${rol}`);
      setForm(prev => ({ ...prev, rol, permisos: res.data.permisos }));
    } catch {
      setForm(prev => ({ ...prev, rol }));
    }
  };

  const handleSaveTemplate = async () => {
    if (templateNombre.trim().length < 3) {
      toast.error('Nombre debe tener al menos 3 caracteres');
      return;
    }
    try {
      await api.post('/admin/roles-custom', {
        nombre: templateNombre.trim(),
        descripcion: templateDescripcion.trim(),
        permisos: form.permisos,
        rol_base: form.rol,
      });
      toast.success(`Plantilla "${templateNombre}" guardada`);
      setSaveTemplateOpen(false);
      setTemplateNombre(''); setTemplateDescripcion('');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar plantilla');
    }
  };

  const handleDeleteTemplate = async (slug, nombre) => {
    if (!window.confirm(`¿Eliminar plantilla "${nombre}"?\nLos usuarios con este rol mantendrán sus permisos actuales.`)) return;
    try {
      await api.delete(`/admin/roles-custom/${slug}`);
      toast.success(`Plantilla "${nombre}" eliminada`);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  const openCreate = async () => {
    const permsRes = await api.get('/admin/default-permissions/consulta');
    setForm({ email: '', nombre: '', password: '', rol: 'consulta', permisos: permsRes.data.permisos });
    setModal({ open: true, mode: 'create', user: null });
  };

  const openEdit = (user) => {
    setForm({
      email: user.email, nombre: user.nombre, password: '',
      rol: user.rol, permisos: user.permisos || {},
    });
    setModal({ open: true, mode: 'edit', user });
  };

  const togglePerm = (moduleId, perm) => {
    setForm(prev => {
      const perms = { ...prev.permisos };
      if (!perms[moduleId]) perms[moduleId] = { ver: false, editar: false, vb: false };
      perms[moduleId] = { ...perms[moduleId], [perm]: !perms[moduleId][perm] };
      return { ...prev, permisos: perms };
    });
  };

  // Toggle acceso a un subitem (path) dentro de un módulo.
  // Si `paths` está vacío/undefined → se asume acceso a todos los subitems.
  // Si está definido → sólo los paths listados son accesibles.
  const toggleSubPath = (moduleId, path, allPaths) => {
    setForm(prev => {
      const perms = { ...prev.permisos };
      if (!perms[moduleId]) perms[moduleId] = { ver: true, editar: false, vb: false };
      const current = Array.isArray(perms[moduleId].paths) ? perms[moduleId].paths : null;
      let nextPaths;
      if (current === null) {
        // Estaban "todos". Al destickar uno, empezamos con el resto.
        nextPaths = allPaths.filter(p => p !== path);
      } else if (current.includes(path)) {
        nextPaths = current.filter(p => p !== path);
      } else {
        nextPaths = [...current, path];
      }
      // Si quedan TODOS marcados, limpiamos el array (equivale a "todos").
      // Si quedan 0, dejamos array vacío explícito (= ningún sub-item visible, módulo oculto del sidebar).
      if (nextPaths.length === allPaths.length) {
        const { paths: _omit, ...rest } = perms[moduleId];
        perms[moduleId] = rest;
      } else {
        perms[moduleId] = { ...perms[moduleId], paths: nextPaths };
      }
      return { ...prev, permisos: perms };
    });
  };

  const [expandedMod, setExpandedMod] = useState({});
  const toggleExpandMod = (id) => setExpandedMod(prev => ({ ...prev, [id]: !prev[id] }));

  const handleSave = async () => {
    if (!form.nombre || !form.email) { toast.error('Complete nombre y email'); return; }
    if (modal.mode === 'create' && !form.password) { toast.error('Ingrese contraseña'); return; }
    setSaving(true);
    try {
      if (modal.mode === 'create') {
        await api.post('/admin/users', form);
        toast.success('Usuario creado');
      } else {
        const update = { nombre: form.nombre, rol: form.rol, permisos: form.permisos };
        if (form.password) update.password = form.password;
        await api.put(`/admin/users/${modal.user.id}`, update);
        toast.success('Usuario actualizado');
      }
      setModal({ open: false, mode: 'create', user: null });
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const handleToggleActive = async (user) => {
    try {
      await api.put(`/admin/users/${user.id}`, { activo: !user.activo });
      toast.success(user.activo ? 'Usuario desactivado' : 'Usuario activado');
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`¿Eliminar usuario ${user.nombre || user.email}?`)) return;
    try {
      const res = await api.delete(`/admin/users/${user.id}`);
      toast.success(res.data?.message || 'Usuario eliminado');
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  const getRolBadge = (rol) => {
    const r = ROLES.find(x => x.val === rol);
    return <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${r?.color || 'bg-gray-100'}`}>{r?.label || rol}</span>;
  };

  return (
    <div className="space-y-5" data-testid="gestion-usuarios-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="w-6 h-6 text-indigo-600" />
            Gestión de Usuarios
          </h1>
          <p className="text-sm text-muted-foreground">Crear usuarios y definir permisos por módulo.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button onClick={openCreate} className="bg-indigo-600 hover:bg-indigo-700">
            <Plus className="w-4 h-4 mr-2" /> Nuevo Usuario
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>
      ) : (
        <div className="border rounded-lg" style={{ overflowX: 'clip' }}>
          <table className="w-full text-xs">
            <thead className="sticky top-14 lg:top-0 z-20">
              <tr className="bg-gray-800 text-white">
                <th className="px-3 py-2">Nombre</th>
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2 text-center">Rol</th>
                <th className="px-3 py-2 text-center">Estado</th>
                <th className="px-3 py-2 text-center">Permisos</th>
                <th className="px-3 py-2 text-center">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const permCount = Object.values(u.permisos || {}).filter(p => p.ver || p.editar || p.vb).length;
                return (
                  <tr key={u.id} className={`border-t hover:bg-gray-50 ${!u.activo ? 'opacity-50' : ''}`}>
                    <td className="px-3 py-2 font-medium">{u.nombre}</td>
                    <td className="px-3 py-2 text-gray-600">{u.email}</td>
                    <td className="px-3 py-2 text-center">{getRolBadge(u.rol)}</td>
                    <td className="px-3 py-2 text-center">
                      <button onClick={() => handleToggleActive(u)}
                        className={`px-2 py-0.5 rounded text-[10px] font-medium ${u.activo ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {u.activo ? 'Activo' : 'Inactivo'}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-center text-gray-500">{permCount} módulos</td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex gap-1 justify-center">
                        <button onClick={() => openEdit(u)} className="text-blue-600 hover:text-blue-800 p-1" title="Editar">
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => handleDelete(u)} className="text-red-500 hover:text-red-700 p-1" title="Eliminar">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={modal.open} onOpenChange={(o) => !saving && setModal(prev => ({ ...prev, open: o }))}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-indigo-600" />
              {modal.mode === 'create' ? 'Nuevo Usuario' : `Editar: ${form.nombre}`}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Basic info */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1">Nombre *</label>
                <Input value={form.nombre} onChange={(e) => setForm(prev => ({ ...prev, nombre: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Email *</label>
                <Input type="email" value={form.email} disabled={modal.mode === 'edit'}
                  onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">{modal.mode === 'create' ? 'Contraseña *' : 'Nueva contraseña'}</label>
                <Input type="password" value={form.password} placeholder={modal.mode === 'edit' ? 'Dejar vacío para no cambiar' : ''}
                  onChange={(e) => setForm(prev => ({ ...prev, password: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Rol</label>
                <div className="flex items-center gap-1">
                  <select value={form.rol} onChange={(e) => loadDefaultPerms(e.target.value)}
                    className="w-full h-9 px-3 rounded-md border border-input text-sm"
                    data-testid="select-rol">
                    <optgroup label="Roles preset">
                      {ROLES.map(r => <option key={r.val} value={r.val}>{r.label}</option>)}
                    </optgroup>
                    {customRoles.length > 0 && (
                      <optgroup label="Plantillas custom">
                        {customRoles.map(cr => (
                          <option key={cr.id} value={cr.id}>★ {cr.nombre}</option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                  {(form.rol || '').startsWith('custom_') && (
                    <button
                      type="button"
                      onClick={() => {
                        const cr = customRoles.find(c => c.id === form.rol);
                        if (cr) handleDeleteTemplate(cr.id, cr.nombre);
                      }}
                      className="h-9 px-2 rounded border border-red-200 text-red-600 hover:bg-red-50"
                      title="Eliminar plantilla"
                      data-testid="btn-delete-template"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Permissions matrix */}
            <div>
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4" /> Permisos por Módulo
                </h3>
                <div className="flex items-center gap-2 flex-wrap">
                  {form.rol !== 'admin' && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setSaveTemplateOpen(true)}
                      className="h-7 text-xs gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                      data-testid="btn-save-template"
                      title="Guardar la configuración actual como una plantilla reutilizable"
                    >
                      <Save className="w-3.5 h-3.5" /> Guardar como plantilla
                    </Button>
                  )}
                  {form.rol !== 'admin' && users.length > 0 && (
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-muted-foreground">Copiar de:</label>
                      <select
                        defaultValue=""
                        onChange={(e) => {
                          const src = users.find(u => u.email === e.target.value);
                          if (src && src.permisos) {
                            const cloned = JSON.parse(JSON.stringify(src.permisos));
                            setForm(prev => ({ ...prev, permisos: cloned }));
                            toast.success(`Permisos copiados de ${src.nombre || src.email}`);
                          }
                          e.target.value = '';
                        }}
                        className="h-7 px-2 rounded border border-input text-xs bg-white max-w-[260px]"
                        data-testid="copiar-permisos-select"
                      >
                        <option value="">— Elegir usuario —</option>
                        {users
                          .filter(u => u.email !== form.email)
                          .sort((a, b) => (a.nombre || a.email).localeCompare(b.nombre || b.email))
                          .map(u => (
                            <option key={u.email} value={u.email}>
                              {(u.nombre || u.email)} ({u.rol})
                            </option>
                          ))}
                      </select>
                    </div>
                  )}
                </div>
              </div>
              {form.rol === 'admin' ? (
                <div className="bg-green-50 border border-green-200 rounded p-3 text-sm text-green-800">
                  <CheckCircle className="w-4 h-4 inline mr-1" /> Administrador tiene acceso total a todos los módulos.
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="px-3 py-2 text-left">Módulo</th>
                        <th className="px-3 py-2 text-center w-20">Ver</th>
                        <th className="px-3 py-2 text-center w-20">Editar</th>
                        <th className="px-3 py-2 text-center w-20">VB/Aprobar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {modules.map(mod => {
                        const perms = form.permisos?.[mod.id] || {};
                        const menuDef = menuStructure.find(m => m.id === mod.id);
                        const subItems = menuDef?.subItems || [];
                        const allPaths = subItems.map(s => s.path);
                        const activePaths = Array.isArray(perms.paths) ? perms.paths : null;
                        const isExpanded = !!expandedMod[mod.id];
                        return (
                          <React.Fragment key={mod.id}>
                          <tr className="border-t">
                            <td className="px-3 py-2 font-medium">
                              {subItems.length > 0 ? (
                                <button
                                  type="button"
                                  onClick={() => toggleExpandMod(mod.id)}
                                  className="inline-flex items-center gap-1 hover:text-indigo-600"
                                  data-testid={`expand-mod-${mod.id}`}
                                >
                                  {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                                  {mod.label}
                                  {activePaths && (
                                    <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700">
                                      {activePaths.length}/{allPaths.length}
                                    </span>
                                  )}
                                </button>
                              ) : (
                                mod.label
                              )}
                            </td>
                            {['ver', 'editar', 'vb'].map(perm => (
                              <td key={perm} className="px-3 py-2 text-center">
                                <button onClick={() => togglePerm(mod.id, perm)}
                                  className={`w-6 h-6 rounded border-2 flex items-center justify-center mx-auto transition-all ${
                                    perms[perm] ? 'bg-indigo-500 border-indigo-500 text-white' : 'border-gray-300 hover:border-indigo-400'
                                  }`}>
                                  {perms[perm] && <CheckCircle className="w-4 h-4" />}
                                </button>
                              </td>
                            ))}
                          </tr>
                          {isExpanded && perms.ver && (
                            <tr className="bg-indigo-50/40">
                              <td colSpan={4} className="px-4 py-2 space-y-2">
                                {subItems.length > 0 && (
                                  <div>
                                    <div className="flex items-center justify-between mb-1">
                                      <div className="text-[11px] text-muted-foreground">
                                        Items del menú visibles ({(activePaths === null ? allPaths.length : activePaths.length)}/{allPaths.length}):
                                      </div>
                                      <div className="flex gap-1">
                                        <button
                                          type="button"
                                          onClick={() => setForm(prev => ({
                                            ...prev,
                                            permisos: { ...prev.permisos, [mod.id]: { ...perms, paths: undefined } },
                                          }))}
                                          className="text-[10px] px-2 py-0.5 rounded border border-gray-300 hover:bg-gray-50"
                                          data-testid={`paths-todos-${mod.id}`}
                                        >
                                          Todos
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => setForm(prev => ({
                                            ...prev,
                                            permisos: { ...prev.permisos, [mod.id]: { ...perms, paths: [] } },
                                          }))}
                                          className="text-[10px] px-2 py-0.5 rounded border border-red-200 text-red-700 hover:bg-red-50"
                                          data-testid={`paths-ninguno-${mod.id}`}
                                        >
                                          Ninguno
                                        </button>
                                      </div>
                                    </div>
                                    <p className="text-[10px] text-gray-500 mb-1.5 italic">
                                      Marcá solo lo que el usuario debe ver del menú. Si no marcás ninguno, el módulo queda oculto.
                                    </p>
                                    <div className="grid grid-cols-2 gap-1">
                                      {subItems.map(sub => {
                                        const checked = activePaths === null ? true : activePaths.includes(sub.path);
                                        return (
                                          <label key={sub.path} className="flex items-center gap-2 text-xs cursor-pointer hover:text-indigo-700">
                                            <input
                                              type="checkbox"
                                              checked={checked}
                                              onChange={() => toggleSubPath(mod.id, sub.path, allPaths)}
                                              className="w-3.5 h-3.5 accent-indigo-600"
                                              data-testid={`subpath-${mod.id}-${sub.path.replace(/\//g, '_')}`}
                                            />
                                            <span className={checked ? '' : 'line-through opacity-50'}>{sub.label}</span>
                                          </label>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                                {/* Acciones granulares (NEW) */}
                                {(moduleActions[mod.id] || []).length > 0 && (
                                  <div className="border-t pt-2">
                                    <div className="text-[11px] text-muted-foreground mb-1 font-semibold">
                                      Acciones específicas:
                                    </div>
                                    <div className="grid grid-cols-2 gap-1">
                                      {(moduleActions[mod.id] || []).map(act => {
                                        const aChecked = !!(perms.acciones || {})[act.id];
                                        return (
                                          <label key={act.id} className="flex items-center gap-2 text-xs cursor-pointer hover:text-emerald-700"
                                            data-testid={`accion-${mod.id}-${act.id}`}>
                                            <input
                                              type="checkbox"
                                              checked={aChecked}
                                              onChange={() => {
                                                const cur = perms.acciones || {};
                                                const next = { ...cur, [act.id]: !aChecked };
                                                setForm(prev => ({
                                                  ...prev,
                                                  permisos: {
                                                    ...prev.permisos,
                                                    [mod.id]: { ...perms, acciones: next },
                                                  },
                                                }));
                                              }}
                                              className="w-3.5 h-3.5 accent-emerald-600"
                                            />
                                            <span className={aChecked ? 'text-emerald-700 font-medium' : ''}>{act.label}</span>
                                          </label>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModal({ open: false, mode: 'create', user: null })}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {modal.mode === 'create' ? 'Crear Usuario' : 'Guardar Cambios'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Guardar como plantilla custom */}
      <Dialog open={saveTemplateOpen} onOpenChange={setSaveTemplateOpen}>
        <DialogContent className="max-w-md" data-testid="save-template-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <Save className="w-5 h-5" /> Guardar como Plantilla
            </DialogTitle>
            <DialogDescription>
              La configuración actual de permisos quedará disponible como un rol reutilizable para futuros usuarios.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium mb-1 block">Nombre de la plantilla *</label>
              <input
                value={templateNombre}
                onChange={(e) => setTemplateNombre(e.target.value)}
                placeholder="Ej: Capataz Obra Norte"
                className="w-full h-9 px-3 rounded-md border border-input text-sm"
                data-testid="template-nombre"
                autoFocus
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Descripción (opcional)</label>
              <textarea
                value={templateDescripcion}
                onChange={(e) => setTemplateDescripcion(e.target.value)}
                placeholder="Para qué usuarios sirve esta plantilla."
                rows={2}
                className="w-full px-3 py-2 rounded-md border border-input text-sm resize-none"
                data-testid="template-descripcion"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setSaveTemplateOpen(false)}>Cancelar</Button>
            <Button onClick={handleSaveTemplate} disabled={templateNombre.trim().length < 3}
              className="bg-emerald-600 hover:bg-emerald-700 gap-1.5"
              data-testid="template-confirmar">
              <Save className="w-4 h-4" /> Guardar plantilla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
